import { useQuery } from "@tanstack/react-query"
import debounce from "debounce"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useMemo, useState } from "react"

import {
  Button,
  cn,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@atlant/ui"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { client } from "#utils/graphql-client.ts"

const AirportsByTermDocument = graphql(/* GraphQL */ `
  query AirportGetByTerm($input: QueryAirportGetByTermInput!) {
    airportGetByTerm(input: $input) {
      id
      source
      name
      code
      country
      city
      tz
    }
  }
`)

export interface Airport {
  id: string
  source: "PLATFORM" | "MANUAL" | "LEON"
  name: string
  code: string
  country: string
  city: string
  tz: string
}

export const useAirportSearch = (sources: Airport["source"][]) => {
  const [searchTerm, setSearchTerm] = useState("")
  const {
    me: { organization },
  } = useLoggedInUserInfo()
  const search = useMemo(
    () =>
      debounce((term: string) => {
        setSearchTerm(term.length > 2 ? term : "")
      }, 400),
    [],
  )

  const { data } = useQuery({
    queryKey: queryKeys.airports.search(searchTerm),
    queryFn: () =>
      client.request(AirportsByTermDocument, {
        input: {
          sources,
          term: searchTerm,
          organizationId: organization.id,
        },
      }),
    enabled: searchTerm.length > 3,
  })

  return { airports: data?.airportGetByTerm ?? [], search }
}

interface FormAirportSelectProps {
  sources: ("PLATFORM" | "MANUAL" | "LEON")[]
  value: string
  onChange: (value: string) => void
  name?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: string
  initialLabel?: string
  onAirportSelect?: (airport: Airport) => void
}

export const FormAirportSelect = ({
  sources,
  value,
  onChange,
  name,
  label,
  placeholder = "Select airport",
  disabled,
  className,
  error,
  initialLabel,
  onAirportSelect,
}: FormAirportSelectProps) => {
  const [open, setOpen] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const { airports, search: handleInputChange } = useAirportSearch(sources)

  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  const displayLabel = selectedLabel ?? (value ? initialLabel : null)

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id} className="text-muted-foreground text-sm font-medium">
          {label}
        </FieldLabel>
      )}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            type="button"
            aria-invalid={error ? true : undefined}
            className={cn(
              "border-input hover:bg-input/10 h-10 w-full justify-between gap-2" +
                " truncate px-3 text-left font-normal",
              !value && "text-muted-foreground",
              error && "border-destructive dark:border-destructive",
            )}
          >
            <span className="truncate">
              {displayLabel ?? <span className="text-placeholder">{placeholder}</span>}
            </span>
            <ChevronsUpDownIcon
              size={16}
              className="text-muted-foreground/80 shrink-0"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="border-input w-(--radix-popper-anchor-width) p-1"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search airport..."
              onValueChange={handleInputChange}
            />
            <CommandList>
              {airports.length > 0 && (
                <CommandGroup>
                  {airports.map((airport) => (
                    <CommandItem
                      key={airport.id}
                      value={airport.id}
                      onSelect={() => {
                        onChange(airport.id)
                        setSelectedLabel(`${airport.city} (${airport.code})`)
                        onAirportSelect?.(airport)
                        setOpen(false)
                      }}
                    >
                      <span className="flex-1">
                        {airport.city} ({airport.code}) — {airport.name}
                      </span>
                      {value === airport.id && (
                        <CheckIcon size={16} className="ml-auto" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
